import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { expenseAPI, metaAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { toast } from 'sonner';
import SearchableSelect from '@/components/SearchableSelect';
import TableTopControls from "../../components/TableTopControls";
import TableBottomControls from "../../components/TableBottomControls";
import { Plus, Edit, Trash2 } from 'lucide-react';
import ConfirmModal from "../../components/ConfirmModal";
import useTime from "../../hooks/UseTime";
import { Calendar } from "@/components/ui/calendar";

export default function ExpensesAdmin() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const { formatDateTime, fromNow } = useTime();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ category: '', amount: '', description: '', vendor_name: '' });
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [editingExpense, setEditingExpense] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({category: '',from_date: null,to_date: null});
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [open, setOpen] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const formatRange = (from, to) => {
    if (!from && !to) return "";
    if (from && !to) {
      return `${from.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })} onwards`;
    }

    if (!from && to) {
      return `Until ${to.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      })}`;
    }

    const optionsDay = { day: "2-digit" };
    const optionsMonth = { month: "short" };
    const optionsFull = { day: "2-digit", month: "short" };
    const optionsYear = { year: "numeric" };

    const sameDay =
      from.toDateString() === to.toDateString();

    const sameMonth =
      from.getMonth() === to.getMonth() &&
      from.getFullYear() === to.getFullYear();

    const sameYear =
      from.getFullYear() === to.getFullYear();

    if (sameDay) {
      return from.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }

    if (sameMonth) {
      return `${from.toLocaleDateString("en-GB", optionsDay)}–${to.toLocaleDateString("en-GB", optionsDay)} ${to.toLocaleDateString("en-GB", optionsMonth)}`;
    }

    if (sameYear) {
      return `${from.toLocaleDateString("en-GB", optionsFull)} → ${to.toLocaleDateString("en-GB", optionsFull)}`;
    }

    return `${from.toLocaleDateString("en-GB", {
      ...optionsFull,
      ...optionsYear
    })} → ${to.toLocaleDateString("en-GB", {
      ...optionsFull,
      ...optionsYear
    })}`;
  };

  const formatDate = (date) => {
    if (!date) return undefined;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".date-picker")) {
        setOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const fetchMeta = async () => {
    try {
      const [categoriesRes] = await Promise.all([
        metaAPI.getExpenseCategories()
      ]);

      setExpenseCategories(categoriesRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);

      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const [res] = await Promise.all([
        expenseAPI.get({
          page,
          page_size: pageSize,
          tz,
          ...appliedFilters,
          from_date: formatDate(appliedFilters.from_date),
          to_date: formatDate(appliedFilters.to_date),
        })
      ]);

      setExpenses(res.data.items);
      setTotalPages(res.data.total_pages);
      setTotalRecords(res.data.total_items);

    } catch (error) {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, appliedFilters]);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);
  useEffect(() => { fetchMeta(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

      if(formData.category === '') {
        toast.error('Category is required');
        return;
      }

      if(!formData.amount || formData.amount<1) {
        toast.error('Invalid amount');
        return;
      }

      if(formData.description === '') {
        toast.error('Description is required');
        return;
      }

      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if(editingExpense) {
        await expenseAPI.update(editingExpense.id, data);
        toast.success('Expense updated successfully');
      } else {
        await expenseAPI.create({ ...formData, amount: parseFloat(formData.amount) });
        toast.success('Expense recorded successfully');
      }

      setShowForm(false);
      setEditingExpense(null);
      setFormData({ category: '', amount: '', description: '', vendor_name: '' });
      fetchExpenses();
    } catch (error) {
      toast.error('Failed to save expense');
    }
  };

  const handleEdit = (e) => {
    setEditingExpense(e);
    setFormData({
      category: e.category,
      amount: e.amount,
      description: e.description,
      vendor_name: e.vendor_name
    });
    setShowForm(true);
  };

  const handleReset = () => {
    const empty = {
      category: '',
      from_date: null,
      to_date: null
    };

    setFilters(empty);
    setAppliedFilters(empty);
    setPage(1);
  };

  const handleSearch = () => {
    setPage(1);
    setAppliedFilters(filters);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await expenseAPI.delete(deleteId);
      toast.success("Expense deleted successfully");
      fetchExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete sale');
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
    }
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  return (
    <AdminLayout activeItem="Expenses">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-medium text-brand-primary font-outfit">Expenses</h1>
          </div>
          {!isSuperAdmin && (
            <button
              onClick={() => { 
                setShowForm(true);
                setEditingExpense(null);
                setFormData({ category: '', amount: '', description: '', vendor_name: '' });
              }}
              className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
              data-testid="add-expense-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Expense
            </button>
          )}
        </div>
      </header>

      <main className="p-8">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="expense-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <div className="bg-white p-8 max-w-md w-full mx-4 border border-slate-200">
              <h2 className="text-xl font-medium text-brand-primary mb-6">Record Expense</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
                  <SearchableSelect
                    value={formData.category}
                    onChange={(val) => setFormData({ ...formData, category: val })}
                    options={expenseCategories}
                    placeholder="Select Category"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Amount (Rs.)</label>
                  <input
                    type="number"
                    min="1"
                    required
                    step="1"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                    data-testid="expense-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Name (Optional)</label>
                  <input
                    type="text"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent focus:border-brand-accent"
                    data-testid="expense-vendor-input"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="expense-submit-button">
                    {editingExpense ? 'Update' : 'Record'}
                  </button>
                  <button type="button" onClick={() => {setShowForm(false); setEditingExpense(null);}} className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200">
            <div className="bg-slate-50 p-4 border-b border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-end">

                <div>
                  <label className="block text-sm text-slate-500 mb-1">Category</label>
                  <SearchableSelect
                    value={filters.category}
                    onChange={(val) => setFilters({ ...filters, category: val })}
                    options={expenseCategories}
                    placeholder="Category"
                  />
                </div>

                <div className="relative date-picker">
                  <label className="block text-sm text-slate-500 mb-1">Date Range</label>

                  <button
                    onClick={() => setOpen(!open)}
                    className={`w-full px-3 py-2 border border-slate-200 bg-white flex items-center justify-between overflow-hidden ${
                      filters.from_date && filters.to_date
                        ? "text-slate-800"
                        : "text-slate-400"
                    }`}
                  >
                    <span className="truncate">
                      {filters.from_date || filters.to_date
                        ? formatRange(filters.from_date, filters.to_date)
                        : "Select date range"}
                    </span>

                    <svg
                      className={`w-4 h-4 text-slate-500 transition-transform ml-2 shrink-0 ${
                        open ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {open && (
                    <div className="absolute z-50 mt-2 bg-white border rounded-md shadow-lg">
                      <Calendar
                        mode="range"
                        selected={{
                          from: filters.from_date,
                          to: filters.to_date
                        }}
                        onSelect={(range) => {
                          setFilters({
                            ...filters,
                            from_date: range?.from || null,
                            to_date: range?.to || null
                          });

                          // optional: auto close when range selected
                          if (range?.from && range?.to) setOpen(false);
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-2 text-center">
                  <button
                    onClick={handleSearch}
                    className="flex-1 bg-brand-accent text-white px-3 py-2 text-sm hover:bg-brand-accent-hover"
                  >
                    Search
                  </button>

                  <button
                    onClick={handleReset}
                    className="flex-1 border border-slate-300 text-slate-700 px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    Reset
                  </button>
                </div>

              </div>
            </div>
            <div className="overflow-x-auto">
              <TableTopControls
                rowsPerPage={pageSize}
                setRowsPerPage={(val) => {
                  setPageSize(val);
                  setPage(1);
                }}
              />
              <table className="w-full" data-testid="expenses-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">
                        Franchise
                      </th>
                    )}
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Category</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Amount</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Description</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Vendor</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Date / Time</th>
                    {!isSuperAdmin && (
                      <th className="px-6 py-4 text-left text-sm font-medium text-slate-700">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-slate-500">No expenses found.</td>
                    </tr>
                  ) : (
                    expenses.map((expense) => (
                      <tr key={expense.id} className="border-b border-slate-100 hover:bg-slate-50">
                        {isSuperAdmin && (
                          <td className="px-6 py-4 text-sm text-slate-600">
                            {expense.franchise_name || '-'}
                          </td>
                        )}
                        <td className="px-6 py-4 text-sm">
                          <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-medium">
                            {expense.category.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-red-600">Rs. {expense.amount.toLocaleString()}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{expense.description}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{expense.vendor_name || '-'}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-800">{formatDateTime(expense.created_at)}</td>
                        {!isSuperAdmin && (
                          <td className="px-6 py-4 text-left text-sm text-slate-600">
                            <button onClick={() => handleEdit(expense)}
                              className="inline-flex items-center py-1 text-sm text-brand-accent hover:text-brand-accent-hover mr-2"
                              data-testid={`edit-sale-${expense.id}`}>
                              <Edit className="h-4 w-4 mr-1" /> Edit
                            </button>
                            <button onClick={() => handleDeleteClick(expense.id)}
                              className="inline-flex items-center pl-3 py-1 text-sm text-red-600 hover:text-red-700"
                              data-testid={`delete-sale-${expense.id}`}>
                              <Trash2 className="h-4 w-4 mr-1" /> Delete
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <TableBottomControls
                currentPage={page}
                totalPages={totalPages}
                setCurrentPage={setPage}
                startItem={(page - 1) * pageSize + 1}
                endItem={Math.min(page * pageSize, totalRecords)}
                totalItems={totalRecords}
              />
            </div>
          </div>
        )}
        <ConfirmModal
          open={confirmOpen}
          title="Delete Expense"
          message="Are you sure you want to delete this expense? This action cannot be undone."
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          confirmText="Delete"
        />
      </main>
    </AdminLayout>
  );
}
